import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { Criticita, Supplier, SupplierKind, SupplierWrite } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { CRITICITA } from './BusinessApps'
import { ErrorBanner } from './common'
import { Finestra } from './ModificheGitlab'
import { CRITICALITY_LABEL } from '../pages/Services'

/**
 * What the pages share about suppliers (supplier_service): the labels, the
 * form to create or edit one, and the selector that links a contract, an
 * asset or a CRA product to a supplier of the registry.
 */

export const TIPI_FORNITORE: SupplierKind[] = ['software', 'hardware', 'cloud', 'ict_service', 'maintenance', 'medical', 'reseller']

export function etichettaTipoFornitore(k: SupplierKind): string {
  switch (k) {
    case 'software': return t('Software')
    case 'hardware': return t('Hardware')
    case 'cloud': return t('Cloud')
    case 'ict_service': return t('Servizi ICT')
    case 'maintenance': return t('Manutenzione')
    case 'medical': return t('Elettromedicali')
    case 'reseller': return t('Rivenditore')
  }
}

export function etichettaSostituibilita(v: 'easy' | 'difficult' | 'impossible'): string {
  return { easy: t('facile'), difficult: t('difficile'), impossible: t('impossibile') }[v]
}

function vuoto(): SupplierWrite {
  return {
    name: '', legal_name: '', vat_number: '', lei: '', country: '', website: '', kinds: [], status: 'active',
    security_contact: '', psirt_url: '', csaf_url: '', owner: '', owner_email: '', language: 'it', notes: '',
    criticality_override: null, ict_provider: false, substitutability: null, exit_strategy: '', exit_tested: false,
    data_locations: [],
  }
}

/** Create or edit a supplier. */
export function FinestraFornitore({ esistente, onAnnulla, onFatto }: {
  esistente?: Supplier
  onAnnulla: () => void
  onFatto: (s: Supplier) => void
}) {
  const [v, setV] = useState<SupplierWrite>(() => esistente ? {
    name: esistente.name, legal_name: esistente.legal_name, vat_number: esistente.vat_number, lei: esistente.lei,
    country: esistente.country, website: esistente.website, kinds: esistente.kinds, status: esistente.status,
    security_contact: esistente.security_contact, psirt_url: esistente.psirt_url, csaf_url: esistente.csaf_url,
    owner: esistente.owner, owner_email: esistente.owner_email, language: esistente.language, notes: esistente.notes,
    criticality_override: esistente.criticality_override, ict_provider: esistente.ict_provider,
    substitutability: esistente.substitutability, exit_strategy: esistente.exit_strategy, exit_tested: esistente.exit_tested,
    data_locations: esistente.data_locations,
  } : vuoto())
  const [sedi, setSedi] = useState((esistente?.data_locations ?? []).join(', '))
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const set = (k: keyof SupplierWrite) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value })

  async function salva() {
    setBusy(true)
    setErrore(null)
    try {
      const corpo = { ...v, data_locations: sedi.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean) }
      onFatto(esistente ? await api.updateSupplier(esistente.id, corpo) : await api.createSupplier(corpo))
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  const tipi = v.kinds ?? []
  return (
    <Finestra titolo={esistente ? t('Modifica il fornitore') : t('Nuovo fornitore')} onChiudi={onAnnulla}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        <h4 className="form__sezione">{t('Anagrafica')}</h4>
        <div className="form-grid">
          <label className="field"><span className="field__label">{t('Nome')}</span>
            <input type="text" value={v.name ?? ''} onChange={set('name')} required /></label>
          <label className="field"><span className="field__label">{t('Ragione sociale')}</span>
            <input type="text" value={v.legal_name ?? ''} onChange={set('legal_name')} /></label>
          <label className="field span-1"><span className="field__label">{t('Partita IVA')}</span>
            <input type="text" className="mono" value={v.vat_number ?? ''} onChange={set('vat_number')} /></label>
          <label className="field span-1"><span className="field__label">{t('Paese')}</span>
            <input type="text" className="mono" value={v.country ?? ''} onChange={set('country')} maxLength={2} placeholder="IT" /></label>
          <label className="field"><span className="field__label">{t('LEI')}</span>
            <input type="text" className="mono" value={v.lei ?? ''} onChange={set('lei')} maxLength={20} />
            <span className="field__hint">{t('Legal Entity Identifier: la chiave del registro delle informazioni DORA.')}</span></label>
          <label className="field span-4"><span className="field__label">{t('Sito')}</span>
            <input type="url" value={v.website ?? ''} onChange={set('website')} placeholder="https://" /></label>
        </div>

        <h4 className="form__sezione">{t('Che cosa fornisce')}</h4>
        <div className="form-toggles" role="group" aria-label={t('Che cosa fornisce')}>
          {TIPI_FORNITORE.map((k) => {
            const on = tipi.includes(k)
            return (
              <button key={k} type="button" className={on ? '' : 'secondary'} aria-pressed={on}
                      onClick={() => setV({ ...v, kinds: on ? tipi.filter((x) => x !== k) : [...tipi, k] })}>
                {etichettaTipoFornitore(k)}
              </button>
            )
          })}
        </div>

        <h4 className="form__sezione">{t('Sicurezza dei prodotti')}</h4>
        <div className="form-grid">
          <label className="field"><span className="field__label">{t('Contatto di sicurezza')}</span>
            <input type="text" value={v.security_contact ?? ''} onChange={set('security_contact')} placeholder={t('es. psirt@produttore.com')} />
            <span className="field__hint">{t('Dove si segnala una vulnerabilità dei suoi prodotti (PSIRT).')}</span></label>
          <label className="field"><span className="field__label">{t('Pagina degli avvisi di sicurezza')}</span>
            <input type="url" value={v.psirt_url ?? ''} onChange={set('psirt_url')} placeholder="https://" /></label>
          <label className="field"><span className="field__label">{t('Avvisi CSAF')}</span>
            <input type="url" value={v.csaf_url ?? ''} onChange={set('csaf_url')} placeholder="https://…/provider-metadata.json" /></label>
        </div>

        <h4 className="form__sezione">{t('Gestione interna')}</h4>
        <div className="form-grid">
          <label className="field"><span className="field__label">{t('Referente interno')}</span>
            <input type="text" value={v.owner ?? ''} onChange={set('owner')} /></label>
          <label className="field"><span className="field__label">{t('Email del referente')}</span>
            <input type="email" value={v.owner_email ?? ''} onChange={set('owner_email')} /></label>
          <label className="field"><span className="field__label">{t('Lingua delle mail')}</span>
            <select value={v.language ?? 'it'} onChange={set('language')}>
              <option value="it">{t('Italiano')}</option><option value="en">{t('Inglese')}</option><option value="fr">{t('Francese')}</option>
            </select></label>
          <label className="field span-4"><span className="field__label">{t('Criticità')}</span>
            <select value={v.criticality_override ?? ''} onChange={(e) => setV({ ...v, criticality_override: (e.target.value || null) as Criticita | null })}>
              <option value="">{esistente?.computed_criticality
                ? t('dai legami diretti: {c}', { c: CRITICALITY_LABEL[esistente.computed_criticality] ?? esistente.computed_criticality })
                : t('dai legami diretti')}</option>
              {CRITICITA.map((c) => <option key={c} value={c}>{CRITICALITY_LABEL[c] ?? c}</option>)}
            </select>
            <span className="field__hint">{t('Calcolata dalle Business App e dai servizi che sostiene direttamente; scegline una solo per correggerla.')}</span></label>
          <label className="field"><span className="field__label">{t('Stato')}</span>
            <select value={v.status ?? 'active'} onChange={set('status')}>
              <option value="active">{t('In uso')}</option><option value="ceased">{t('Cessato')}</option>
            </select></label>
        </div>

        <h4 className="form__sezione">{t('DORA: registro delle informazioni')}</h4>
        <div className="form-grid">
          <div className="checkbox span-6">
            <input id="fornitore-ict" type="checkbox" checked={Boolean(v.ict_provider)}
                   onChange={(e) => setV({ ...v, ict_provider: e.target.checked })} />
            <label htmlFor="fornitore-ict">{t('Fornitore terzo di servizi ICT')}
              <small>{t('anche senza la spunta, i tipi «Cloud» e «Servizi ICT» lo sono')}</small></label>
          </div>
          <label className="field"><span className="field__label">{t('Sostituibilità')}</span>
            <select value={v.substitutability ?? ''} onChange={(e) => setV({ ...v, substitutability: (e.target.value || null) as SupplierWrite['substitutability'] })}>
              <option value="">{t('non valutata')}</option>
              <option value="easy">{t('facile')}</option><option value="difficult">{t('difficile')}</option><option value="impossible">{t('impossibile')}</option>
            </select></label>
          <label className="field span-4"><span className="field__label">{t('Sedi dei dati (paesi)')}</span>
            <input type="text" className="mono" value={sedi} onChange={(e) => setSedi(e.target.value)} placeholder="IT, DE, IE" /></label>
          <label className="field span-6"><span className="field__label">{t('Strategia di uscita')}</span>
            <textarea rows={2} value={v.exit_strategy ?? ''} onChange={set('exit_strategy')}
                      placeholder={t('come si sostituisce, in quanto tempo, dove vanno i dati')} /></label>
          <div className="checkbox span-6">
            <input id="fornitore-uscita" type="checkbox" checked={Boolean(v.exit_tested)}
                   onChange={(e) => setV({ ...v, exit_tested: e.target.checked })} />
            <label htmlFor="fornitore-uscita">{t('La strategia di uscita è stata provata')}</label>
          </div>
        </div>

        <h4 className="form__sezione">{t('Note')}</h4>
        <textarea rows={3} value={v.notes ?? ''} onChange={set('notes')} aria-label={t('Note')} />

        <div className="form-dialog__azioni">
          <button type="button" className="secondary" onClick={onAnnulla} disabled={busy}>{t('Annulla')}</button>
          <button type="button" onClick={() => void salva()} disabled={busy || !(v.name ?? '').trim()}>
            {busy ? t('Salvo…') : esistente ? t('Salva') : t('Crea il fornitore')}
          </button>
        </div>
      </div>
    </Finestra>
  )
}

/**
 * The supplier of the registry for a contract, an asset or a CRA product. The
 * text field stays what it is (the vendor as written); this links it, and
 * CENSO links it by itself when the text is a known name of a supplier.
 */
export function SelettoreFornitore({ value, onChange, disabled }: {
  value: string | null | undefined
  onChange: (id: string | null) => void
  disabled?: boolean
}) {
  const opzioni = useAsync(() => api.supplierOptions().catch(() => []), [])
  return (
    <span className="selettore-fornitore">
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} disabled={disabled}>
        <option value="">{t('riconosciuto dal nome, o nessuno')}</option>
        {(opzioni.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      {value && <Link to={`/suppliers/${value}`} className="eagle-piccolo">{t('scheda')}</Link>}
    </span>
  )
}
